<template>
  <div id="app">
    <TerrorZoneNavBar class="mb-4" @change-language="onLanguageChanged"/>
    <div id="slot-top_mobile_banner" class="mobile-banner hoz-banner"/>
    <div id="slot-top_banner" class="desktop-banner hoz-banner"/>
    <div id="content-wrapper">
      <TerrorZoneHeader
        :language="language"
        :is-online-mode="playMode == 'online'"
        @change-date="onDateChanged"
      />
      <div v-if="errorMessage">
        <h1>Error</h1>
        <p>
          {{ errorMessage }}
        </p>
      </div>
      <div v-else-if="turnstileToken || !antiBot">
        <TerrorZoneQueryTable
          v-if="playMode == 'online'"
          :language="language"
          :play-mode="playMode"
          :selected-timestamp="selectedTimestamp"
          :turnstile-token="turnstileToken"
          :show-immunities="showImmunities"
          :show-exp-tier="showExpTier"
          :show-loot-tier="showLootTier"
          :service-worker="serviceWorker"
          @error="errorMessage = $event"
          @show-install-guide="showInstallGuide"
        />
        <TerrorZoneWeekTable
          v-else-if="playMode == 'offline'"
          :language="language"
          :play-mode="playMode"
          :selected-timestamp="selectedTimestamp"
          :turnstile-token="turnstileToken"
          :show-immunities="showImmunities"
          :show-exp-tier="showExpTier"
          :show-loot-tier="showLootTier"
          @error="errorMessage = $event"
        />
      </div>
      <vue-turnstile
        v-else
        site-key="0x4AAAAAAA4WXTV570zHkMsD"
        theme="dark"
        auto-reset
        @verified="turnstileToken = $event"
      />
    </div>
    <Legends
      @change-immunities="onImmunitiesChanged"
      @show-exp-tier="onExpTierChanged"
      @show-loot-tier="onLootTierChanged"
    />
    <div id="slot-btm_mobile_banner" class="mobile-banner hoz-banner"/>
    <div id="slot-btm_banner" class="desktop-banner hoz-banner"/>
    <TerrorZoneFooter ref='footer' class="mt-4"/>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import VueTurnstile from '@gaviti/vue-turnstile';
import { supportedLanguages, parseParameters } from '@/utils/common'
import TerrorZoneNavBar from './components/NavBar.vue'
import TerrorZoneHeader from './components/Header.vue'
import Legends from './components/Legends.vue'
import TerrorZoneFooter from './components/Footer.vue'
import TerrorZoneQueryTable from './components/TerrorZoneQueryTable.vue'
import TerrorZoneWeekTable from './components/TerrorZoneWeekTable.vue'

const ANTI_BOT = false;

export default {
  name: 'App',

  components: {
    VueTurnstile,
    TerrorZoneNavBar,
    TerrorZoneHeader,
    TerrorZoneQueryTable,
    TerrorZoneWeekTable,
    Legends,
    TerrorZoneFooter,
  },

  data() {
    return {
      language: null,
      selectedTimestamp: null,
      playMode: 'online',
      turnstileToken: null,
      errorMessage: null,
      antiBot: ANTI_BOT,
      showImmunities: false,
      showExpTier: false,
      showLootTier: false,
      serviceWorker: null,
    }
  },

  computed: {
    path () {
      return window.location.pathname;
    },
  },

  async created () {
    const paramStr = window.location.search;
    let showGuide = false;
    if (paramStr?.length > 0) {
      const params = parseParameters(paramStr);
      if (!!params['l'] && supportedLanguages.includes(params['l'])) {
        this.language = params['l'];
      }
      if (!!params['language'] && supportedLanguages.includes(params['language'])) {
        this.language = params['language'];
      }
      if (params['app_guide']) {
        showGuide = true;
      }
    }
    if (!this.language) {
      this.language = localStorage.getItem('language') || 'en-us';
    }
    this.showImmunities = localStorage.getItem('immunities') === 'true';
    this.showExpTier = localStorage.getItem('exp_tier')  === 'true';
    this.showLootTier = localStorage.getItem('loot_tier')  === 'true';
    if (!localStorage.getItem('user_id')) {
      const userId = uuidv4();
      localStorage.setItem('user_id', userId)
    }
    if (this.path === '/online') {
      this.playMode = 'online'
    }
    if (this.path === '/offline') {
      this.playMode = 'offline'
    }
    await this.registerSW();
    if (showGuide) {
      await this.$nextTick();
      this.showInstallGuide();
    }
  },

  methods: {
    onLanguageChanged (language) {
      if (supportedLanguages.includes(language)) {
        this.language = language;
      }
    },

    onDateChanged (newTimestamp) {
      this.selectedTimestamp = newTimestamp;
    },

    onImmunitiesChanged (newValue) {
      this.showImmunities = newValue;
    },

    onExpTierChanged (newValue) {
      this.showExpTier = newValue;
    },

    onLootTierChanged (newValue) {
      this.showLootTier = newValue;
    },

    showInstallGuide () {
      // this.$refs.footer.showInstallGuide();
      window.location.href = "/apps";
    },

    async registerSW () {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        console.log('Service Worker and Push is supported');
        this.serviceWorker = await navigator.serviceWorker.register("/sw.js");
      } else {
        console.warn('Push is not supported');
      }
    },
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: #111;
}

body {
  background-color: #111!important;
}
.inline-block {
  display: inline-block;
}
.pointer {
  cursor: pointer;
}
.left {
  float: left;
}
.right {
  float: right;
}
.fire-text {
  color: #dc3545
}
.cold-text {
  color: #007bff
}
.lightning-text {
  color: #ffc107
}
.poison-text {
  color: #28a745
}
.physical-text {
  color: #918a6b
}
.magic-text {
  color: #d86f23
}
.tab-text {
  --bs-link-color: #aaa;
  --bs-link-hover-color: #fff;
}
.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}
</style>
